import {
  ingredientGroups,
  isAfterBaking,
  isCustomPizzaLine,
  isPizzaProduct,
  komboHalvesOf,
  parseIngredientIds,
  type BuilderIngredient,
} from '@agos/shared'

import type { KitchenDescriptions } from './KitchenItemCard'
import type { KitchenLine } from './kitchen-items'
import { buildRecipeIndex, type RecipeIngredient, type RecipeRow } from './recipes'

/**
 * Everything the kitchen card knows about a product beyond its line - moved out of `KitchenPage` by §9.671 so the
 * rule can be tested apart from the screen (`item-name.ts`'s reason).
 */

interface ProductRow {
  id?: number
  name?: string
  kitchenDescription?: string
  showInKitchen?: boolean
  configPriced?: boolean
  pizzaIngredientIds?: unknown
  /** `standard` or `pizza` - on the products read the board already makes (§9.763). */
  productType?: string
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

/**
 * A dynamická pizza's composition - `FRONTEND.md` §9.671, FB-996, owner #1.
 *
 * **The owner**: *"zloženie pizz neukazuje v kuchyni správne pritom v produktoch zloženie dinamicke je správne."* A
 * product with `configPriced` is made of the ingredients its `pizzaIngredientIds` name - what the product dialog's
 * *Výber ingrediencií* shows ticked - and the kitchen never read either field, so it printed a recipe or a
 * description somebody wrote separately, often before the pizza was made dynamic.
 *
 * **In the dialog's own order**: `ingredientGroups(…, true)` is what draws those chips - base, crust, cheese, meat,
 * vegetables, then the editor's own sort - so the card lists a pizza the way its owner set it up rather than in the
 * order the ids were tapped. **Active ingredients only**, for the same reason: the dialog neither shows nor prices
 * an inactive one, and the card follows the dialog.
 *
 * Product id -> the names joined with commas, which is the shape `mergeIngredientsAndExtras` already reads, so the
 * line's own `[extras]` fold into it exactly as they fold into a description. A product whose ids name nothing
 * readable has no entry, and the card falls back to the old order.
 */
export function configuredCompositions(
  products: readonly ProductRow[],
  ingredients: readonly BuilderIngredient[],
): Record<number, string> {
  const inDialogOrder = ingredientGroups(ingredients, true).flatMap((group) => group.ingredients)
  const configured: Record<number, string> = {}
  for (const product of products) {
    if (product.id == null || product.configPriced !== true) continue
    const chosen = new Set(parseIngredientIds(product.pizzaIngredientIds))
    if (chosen.size === 0) continue
    const names = inDialogOrder
      .filter((ingredient) => chosen.has(ingredient.id))
      .map((ingredient) => ingredient.name.trim())
    if (names.length > 0) configured[product.id] = names.join(', ')
  }
  return configured
}

/**
 * Whether an id is a product the menu answered - §9.671.
 *
 * `buildDescriptions` writes a `showInKitchen` entry for **every** product, whatever its flag, so the key set is the
 * catalogue. Asked as an own property, so a prototype key can never read as a product.
 */
export function isKnownProduct(
  productId: number,
  descriptions: Pick<KitchenDescriptions, 'showInKitchen'>,
): boolean {
  return Object.prototype.hasOwnProperty.call(descriptions.showInKitchen, productId)
}

/**
 * A product's own entry in one of the card's tables, or - for an id that is not a product - its size variant's.
 *
 * **Legacy wrote a size variant as `p * 1000 + n`**, and 9 729 stored lines still carry one, so an id with no entry
 * of its own is looked up again under `Math.floor(id / 1000)`. **That fallback used to apply to every id**, so a real
 * product numbered 1 000 or more with no description borrowed product 1's - another pizza's composition on the card.
 * It applies now only to an id the menu does not know, which is the one case it was written for.
 */
export function productValue<T>(
  table: Readonly<Record<number, T>>,
  productId: number,
  descriptions: Pick<KitchenDescriptions, 'showInKitchen'>,
): T | undefined {
  if (isKnownProduct(productId, descriptions)) return table[productId]
  return table[productId] ?? table[Math.floor(productId / 1000)]
}

/**
 * A name as the after-baking split compares it - §9.673: case and diacritics folded, spaces collapsed, nothing else.
 *
 * **Equality, not containment, and the difference is the point.** *Parmezán* must not pull *Parmezánová omáčka*
 * below the line - a sauce baked with the pizza - and *Mozzarella di bufala* must not pull plain *Mozzarella*. A
 * recipe ingredient named differently from the pizza ingredient (a stock item called *Rukola 1 kg*) therefore does
 * not match, and the owner renames one or the other.
 */
export function foldIngredientName(name: string): string {
  return name.normalize('NFD').replace(/\p{M}+/gu, '').toLowerCase().replace(/\s+/g, ' ').trim()
}

/** The folded names of every ingredient flagged *Po upečení* - §9.673, `V72`. Inactive ones too: still after baking. */
export function afterBakingNames(ingredients: unknown): Set<string> {
  const names = new Set<string>()
  for (const row of asArray<{ name?: unknown }>(ingredients)) {
    if (!isAfterBaking(row) || typeof row.name !== 'string') continue
    const folded = foldIngredientName(row.name)
    if (folded !== '') names.add(folded)
  }
  return names
}

/**
 * A card's chips in two parts, baked with the pizza and put on after it - §9.673, owner #7.
 *
 * Each part keeps the order it had, so a card with nothing flagged is drawn exactly as before - every franchise until
 * somebody ticks *Po upečení*.
 */
export function splitAfterBaking<T>(
  items: readonly T[],
  nameOf: (item: T) => string,
  afterBaking: ReadonlySet<string> | undefined,
): { before: T[]; after: T[] } {
  if (afterBaking === undefined || afterBaking.size === 0) return { before: [...items], after: [] }
  const before: T[] = []
  const after: T[] = []
  for (const item of items) {
    if (afterBaking.has(foldIngredientName(nameOf(item)))) after.push(item)
    else before.push(item)
  }
  return { before, after }
}

/**
 * Whether a catalogue product goes into the oven as a pizza - `FRONTEND.md` §9.763, owner O125.
 *
 * **Its type is `pizza`, or it is a dynamická pizza** (`configPriced`), whatever type it was saved with: a product
 * made of the ingredients *Výber ingrediencií* ticks is made of pizza toppings. The type is asked through
 * `isPizzaProduct`, the till's own rule, without the category half - the products read carries no category name,
 * and the contract is the type. Measured: 30 of Šurany's active products and 29 of Nesvady's are `pizza`, the rest
 * `standard`.
 */
export function productBakesAsPizza(product: Pick<ProductRow, 'productType' | 'configPriced'>): boolean {
  return isPizzaProduct({ productType: product.productType ?? null }) || product.configPriced === true
}

/**
 * Whether a kitchen card is a pizza, so its toppings may split at *Po upečení* - `FRONTEND.md` §9.763, FB-1083.
 *
 * **The owner**: *"that new «Po upečení» is showing also for other products, not only for pizzas, custom pizzas,
 * etc... but also for beef bowl etc... thats a bug"*. §9.673 split every card at the names ticked on the pizza
 * toppings, so a bowl whose recipe names *Rukola* put it under the line. The flag belongs to a pizza topping, and
 * only a pizza goes into the oven.
 *
 * - **A kombo** - each half is a pizza, the kombo builder offers nothing else. Known by `komboHalvesOf`, the helper
 *   both of its writers build the name with, so the answer matches the card's own kombo branch.
 * - **No catalogue row** (`productId` 0 or negative): a custom pizza, known by what its builder writes
 *   (`isCustomPizzaLine`), and nothing else - a lunch dish, a gift or a deposit is not one.
 * - **A catalogue product** by its own entry in `pizzas`, and a legacy size variant (`p * 1000 + n`) by its
 *   product's, through `productValue` - the lookup the card's other tables use. **A product the read did not
 *   answer is not a pizza.**
 */
export function bakesAsPizza(
  line: Pick<KitchenLine, 'productId' | 'productName'>,
  descriptions: Pick<KitchenDescriptions, 'showInKitchen' | 'pizzas'>,
): boolean {
  if (komboHalvesOf(line.productName) !== null) return true
  const productId = line.productId ?? 0
  if (productId <= 0) return isCustomPizzaLine(line.productName)
  return productValue(descriptions.pizzas ?? {}, productId, descriptions) === true
}

export function buildDescriptions(
  products: unknown,
  recipeRows: unknown,
  pizzaIngredients: unknown = [],
): KitchenDescriptions {
  const byId: Record<number, string> = {}
  const byName: Record<string, string> = {}
  const showInKitchen: Record<number, boolean> = {}
  const recipes: Record<number, RecipeIngredient[]> = {}
  const recipesByName: Record<string, RecipeIngredient[]> = {}
  const configuredByName: Record<string, string> = {}
  const pizzas: Record<number, boolean> = {}

  // §9.55 - the rule lives in `recipes.ts` so it can be tested apart from the screen. Product names
  // go in with it: a recipe whose one ingredient *is* the product tells a cook nothing, and 25
  // products on this menu have exactly that.
  const nameByProductId: Record<number, string> = {}
  for (const product of asArray<ProductRow>(products)) {
    if (product.id != null && product.name) nameByProductId[product.id] = product.name
  }
  Object.assign(recipes, buildRecipeIndex(asArray<RecipeRow>(recipeRows), nameByProductId))

  // §9.671 - the pizza ingredients read is the public `GET /pizza-ingredients` the till already makes.
  const configured = configuredCompositions(
    asArray<ProductRow>(products),
    asArray<BuilderIngredient>(pizzaIngredients),
  )

  for (const product of asArray<ProductRow>(products)) {
    if (product.id == null) continue
    showInKitchen[product.id] = product.showInKitchen !== false
    // §9.763 - an entry for every product, as `showInKitchen` has: a bowl answers false for itself.
    pizzas[product.id] = productBakesAsPizza(product)
    // A kombo half is only a name in the line, so the name is the only key available for it.
    const byThisProduct = recipes[product.id]
    if (byThisProduct && product.name) {
      recipesByName[product.name.trim().toLowerCase()] = byThisProduct
    }
    const configuredHere = configured[product.id]
    if (configuredHere !== undefined && product.name) {
      configuredByName[product.name.trim().toLowerCase()] = configuredHere
    }
    if (!product.kitchenDescription) continue
    byId[product.id] = product.kitchenDescription
    if (product.name) byName[product.name.trim().toLowerCase()] = product.kitchenDescription
  }

  return {
    byId,
    byName,
    showInKitchen,
    recipes,
    recipesByName,
    configured,
    configuredByName,
    // §9.673 - which chips go below the line, and §9.763 - on which cards.
    afterBaking: afterBakingNames(pizzaIngredients),
    pizzas,
  }
}
